import { useEffect } from "react";

import { Copy, MarkdownContent, useAuth } from "~components";
import type { LAContactPersonAnswerType } from "~onboarding/utils";
import { translate as t } from "~utils";

type LAContactPersonAnswerProps = LAContactPersonAnswerType & {
  onChange: (value: string) => void;
  isReadOnly?: boolean;
};

export function LAContactPersonAnswer(props: LAContactPersonAnswerProps) {
  const { onChange, isReadOnly, value } = props;

  const { userInfo } = useAuth();
  const subName = userInfo?.name;
  const contactName = userInfo?.lacoeContact?.name;
  const contactPhone = userInfo?.lacoeContact?.phone;
  const contactEmail = userInfo?.lacoeContact?.email;
  const emailMessage = t("LA_CONTACT_PERSON_EMAIL", { subName, contactName });

  // Why this useEffect?
  // This is a pattern used to make the FE onboarding machinery work.
  //
  // An onboarding task configuration lists steps, each with a list of questions the
  // user must answer. Each question must have a `value` associated with it, but some
  // questions don't render an interactive UI element (like a button or an input field)
  // that would allow and event handler to update the global state.
  //
  // For these questions, the task configuration must provide the default value, which
  // is eventually passed into the answer component as the `value` prop. When the
  // component mounts, we pass the `value` prop to the parent so the global state is
  // updated. And when the user clicks the Next button (to move to the next step), the
  // global state is synced to the server, thus completing this question.
  useEffect(() => {
    if (isReadOnly === false) {
      onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-6xl)",
      }}
    >
      <MarkdownContent
        css={{ width: "100%" }}
        body={t("LA_CONTACT_PERSON_INSTRUCTIONS", { contactEmail, emailMessage })}
      />
      <MarkdownContent
        css={{ width: "100%" }}
        body={t("LA_CONTACT_PERSON", { contactName, contactPhone })}
      />
      <div
        css={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          backgroundColor: "var(--swing-white300)",
          borderRadius: "var(--spacing-sm)",
          padding: "var(--spacing-lg)",
          border: "1px solid var(--swing-slate200)",
        }}
      >
        {contactEmail}
        <Copy value={contactEmail} />
      </div>
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--swing-white300)",
          borderRadius: "var(--spacing-sm)",
          padding: "var(--spacing-lg)",
          border: "1px solid var(--swing-slate200)",
        }}
      >
        <div css={{ display: "flex", justifyContent: "flex-end" }}>
          <Copy value={emailMessage} />
        </div>
        <MarkdownContent css={{ width: "100%" }} body={emailMessage} />
      </div>
    </div>
  );
}
